import React from 'react'
import Backdrop from '@mui/material/Backdrop';
import Modal from '@mui/material/Modal';
import Fade from '@mui/material/Fade';
import Button from '@mui/material/Button';
import { Box, useTheme, Typography, List, ListItem } from "@mui/material";
import { tokens } from "../../../theme";
import Axios from 'axios';
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import { getAuthHeader } from '../../../utils/authToken';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

const grid = 8;

const getItemStyle = (isDragging, draggableStyle, colors) => ({
    userSelect: "none",
    padding: `${grid}px ${grid * 2}px`,
    margin: `0 0 ${grid}px 0`,
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: isDragging ? colors.greenAccent[600] : colors.primary[700],
    border: `1px solid ${colors.primary[100]}`,
    ...draggableStyle
});

const getListStyle = (isDraggingOver, colors) => ({
    border: isDraggingOver ? `2px dashed ${colors.greenAccent[500]}` : `2px solid ${colors.primary[100]}`,
    borderRadius: 4,
    padding: grid,
    backgroundColor: colors.primary[600],
});

const ReorderClassMaterialsModal = ({
    courseId,
    classMaterials,
    setSuccessAlertOpen,
    setErrorAlertOpen,
    setSuccessAlertMessage,
    setErrorAlertMessage,
    setBackdropOpen,
}) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [materialList, setMaterialList] = React.useState([]);
    const [materialMap, setMaterialMap] = React.useState({});
    const [loading, setLoading] = React.useState(false);

    // for modal
    const [open, setOpen] = React.useState(false);
    const handleOpen = () => setOpen(true);
    const handleClose = () => { setOpen(false); };

    const handleSave = async (e) => {
        e.preventDefault();
        setBackdropOpen(true);

        await Axios.put(`${process.env.REACT_APP_URL}/api/v1/courses/${courseId}/class-materials/reorder`, {
            orderedIds: materialList,
        }, {
            headers: {
                Authorization: getAuthHeader(),
            },
        }).then(async (response) => {
            setSuccessAlertMessage("Class materials reordered successfully");
            setSuccessAlertOpen(true);
            handleClose();
            window.location.reload();
        }).catch((error) => {
            setErrorAlertMessage(error.response?.data?.message || "Error: Cannot reorder class materials. Please try again.");
            setErrorAlertOpen(true);
        }).finally(() => {
            setBackdropOpen(false);
        });
    };

    React.useEffect(() => {
        if (!classMaterials || classMaterials.length === 0 || !open) return;

        setLoading(true);

        const matMap = {};
        for (const material of classMaterials) {
            matMap[material.id] = material;
        }

        const list = classMaterials.map((material) => material.id);
        // sort by order (already sorted from server, but just in case)
        list.sort((a, b) => (matMap[a].order || 0) - (matMap[b].order || 0));

        setMaterialList(list);
        setMaterialMap(matMap);

        setLoading(false);
    }, [classMaterials, open]);

    const handleOnDragEnd = (result) => {
        const { destination, source } = result;

        if (!destination) return;

        if (destination.droppableId === source.droppableId && destination.index === source.index) {
            return;
        }

        const items = Array.from(materialList);
        const [removed] = items.splice(source.index, 1);
        items.splice(destination.index, 0, removed);

        setMaterialList(items);
    };

    const style = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 600,
        maxHeight: "80vh",
        overflowY: "auto",
        bgcolor: colors.primary[600],
        border: '2px solid #000',
        boxShadow: 24,
        p: 4,
        borderRadius: '8px',
    };

    return (
        <div>
            <Button
                type="submit"
                variant="contained"
                onClick={handleOpen}
                style={{
                    backgroundColor: colors.primary[300],
                    color: "white",
                    width: "14rem",
                    marginTop: "0.5rem",
                    minHeight: "3rem"
                }}
                size='small'
            >
                Reorder Class Materials
            </Button>
            <Modal
                aria-labelledby="transition-modal-title"
                aria-describedby="transition-modal-description"
                open={open}
                onClose={handleClose}
                closeAfterTransition
                slots={{ backdrop: Backdrop }}
                slotProps={{
                    backdrop: {
                        timeout: 500,
                    },
                }}
            >
                <Fade in={open}>
                    <Box sx={style}>
                        <Typography variant="h6" fontWeight="bold" color={colors.primary[450]}>
                            Reorder Class Materials
                        </Typography>
                        <Typography variant="body2" color={colors.primary[300]} mt={1}>
                            Drag and drop to reorder the class materials.
                        </Typography>
                        <Box display="flex" flexDirection="column" gap={2} width="100%" pt={2}>
                            <Box display="flex" flexDirection="column">
                                {loading ? (
                                    <Typography>Loading...</Typography>
                                ) : classMaterials === null || classMaterials.length === 0 ? (
                                    <Typography color={colors.primary[450]}>No class materials available.</Typography>
                                ) : (
                                    <DragDropContext onDragEnd={handleOnDragEnd}>
                                        <Droppable
                                            droppableId="droppable"
                                            renderClone={(provided, snapshot, rubric) => (
                                                <ListItem
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    {...provided.dragHandleProps}
                                                    style={getItemStyle(
                                                        snapshot.isDragging,
                                                        provided.draggableProps.style,
                                                        colors
                                                    )}
                                                >
                                                    <DragIndicatorIcon />
                                                    Material
                                                </ListItem>
                                            )}
                                        >
                                            {(provided, snapshot) => (
                                                <List
                                                    {...provided.droppableProps}
                                                    ref={provided.innerRef}
                                                    style={getListStyle(snapshot.isDraggingOver, colors)}
                                                >
                                                    {materialList && materialList.length > 0 && materialList.map((materialId, index) => {
                                                        const material = materialMap[materialId];

                                                        return (
                                                            <Draggable
                                                                key={materialId}
                                                                draggableId={materialId.toString()}
                                                                index={index}
                                                            >
                                                                {(provided, snapshot) => (
                                                                    <ListItem
                                                                        ref={provided.innerRef}
                                                                        {...provided.draggableProps}
                                                                        {...provided.dragHandleProps}
                                                                        style={getItemStyle(
                                                                            snapshot.isDragging,
                                                                            provided.draggableProps.style,
                                                                            colors
                                                                        )}
                                                                    >
                                                                        <DragIndicatorIcon sx={{ color: colors.primary[300] }} />
                                                                        <Typography sx={{ wordBreak: 'break-word' }}>
                                                                            {material?.name}
                                                                        </Typography>
                                                                    </ListItem>
                                                                )}
                                                            </Draggable>
                                                        );
                                                    })}
                                                    {provided.placeholder}
                                                </List>
                                            )}
                                        </Droppable>
                                    </DragDropContext>
                                )}
                            </Box>
                            <Button
                                type="button"
                                variant="contained"
                                color="primary"
                                sx={{ width: "fit-content" }}
                                onClick={handleSave}
                                disabled={!materialList || materialList.length === 0}
                            >
                                Save Order
                            </Button>
                        </Box>
                    </Box>
                </Fade>
            </Modal>
        </div>
    )
}

export default ReorderClassMaterialsModal
